import { useDataSource } from './use-data-source';
import { buildAst } from '@/utils/ast';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { Shape, ShapeType } from '@/services/shape';
import { WatchfaceService } from '@/services/watchface';
import { xmlToAst } from '@/utils/xmlTool';
import { useLayerStore } from '@/store/use-layer-store';
import { onBeforeUnmount, ref } from 'vue';
import { useImageDoms } from './use-image-doms';
import { useImagesStore } from '@/store/use-images-store';
import { WatchfaceRenderer } from '@/watch-renderer';
import { useShapeVideo } from './use-shape-video';
import { mainRenderer } from './use-canvas-render';
import { getImageSrcs } from '@/utils/ast/utils/resource-image.utils';
import { useDataSourceStore } from '@/store/use-data-source-store';
import { getThemeRive } from '.';
import { sleep } from '@/utils/sleep.utils';
import { canUse } from '@/utils/can-use.utils';

const _existsPointer = (theme?: StoreThemeItem) => {
  const stack = [...(theme?.children ?? [])];
  let hour = false;
  let minute = false;
  let second = false;
  while (stack.length) {
    const layer = stack.pop();
    if (layer?.children) stack.unshift(...layer.children);
    const source = layer?.attrs?.source;
    if (layer?.type == ShapeType.Image) {
      if (source == 'timeHour' || source == 'time') hour = true;
      if (source == 'timeMinute') minute = true;
      if (source == 'timeSecond') second = true;
    }
  }

  return [hour, minute, second].filter((item) => !!item).length > 1;
};

export const useWatchfaceScreenShot = () => {
  let canvasEl: HTMLCanvasElement | undefined;
  let renderer: WatchfaceRenderer | undefined;

  const watchfaceStore = useWatchfaceStore();
  const layerStore = useLayerStore();
  const imageStore = useImagesStore();
  const dataSourceStore = useDataSourceStore();
  const { configData } = useDataSource();
  const { imgsToDoms } = useImageDoms();
  const { toVideoDoms, getVideoSrcsFromAst } = useShapeVideo();

  const timer = ref(0);

  const waitRenderring = async (renderer: WatchfaceRenderer) => {
    let timer = 0;
    renderer.renderring();
    return new Promise<void>((resolve) => {
      const tick = () => {
        const frameCount = renderer.runtime?.rive?.rive.frameCount ?? 0;
        if (frameCount > 6) {
          window.cancelAnimationFrame(timer);
          resolve();
        } else {
          timer = window.requestAnimationFrame(tick);
        }
      };
      tick();
    });
  };

  // 缓存 AST 和图片 DOM，同一 theme 多次 screenshot 时避免重复构建（如 validAODPixels 逐元素截图）
  let _cachedAstKey = '';
  let _cachedAst: Ast | undefined;
  let _cachedImgs: { [key: string]: HTMLImageElement } | undefined;
  let _cachedVideos: any;
  let _cachedDesc: any;

  /**
   * 表盘截图，返回图片base64数据
   * @param watchfacePath [可选] 需要渲染的表盘目录，默认为空，即用当前打开的表盘数据
   * @param shapeIds 需要渲染的图层id,默认为空，渲染全部数据
   * @returns
   */
  const screenshot = async (
    watchfacePath?: string,
    options?: {
      themeName?: string;
      themeType?: 'normal' | 'AOD';
      /**
       * 配色：默认使用第一个配色
       */
      themeColor?: string;
      shapeIds?: string[];
      slotDisplayAll?: WatchRendererOptions['renderTheme']['slotDisplayAll'];
      ignoreBg?: boolean;
      usingCurConfigData?: boolean;
      clipRound?: number;
      usePreviewPointerData?: boolean;
      forceRenderWhenHidden?: boolean;
      returnType?: 'base64' | 'canvas';
      // 忽略所有的旋转
      ignoreRotation?: boolean;
      useMaxValue?: boolean;
      // 是否使用rive预览图
      useRivePreview?: boolean;
    }
  ) => {
    canvasEl = canvasEl ?? document.createElement('canvas');
    if (!canvasEl) return;
    const {
      themeName,
      themeColor,
      themeType = 'normal',
      shapeIds = undefined,
      slotDisplayAll,
      usingCurConfigData = false,
      usePreviewPointerData = false,
      forceRenderWhenHidden = false,
      ignoreRotation = false,
      useMaxValue = false,
      useRivePreview = false
    } = options ?? {};

    const usingCurentWatchface = watchfacePath ? false : true;

    const service = new WatchfaceService(
      watchfacePath ? watchfacePath : watchfaceStore.path
    );
    const _getAst = async () => {
      if (usingCurentWatchface) {
        const ast = mainRenderer?.options.ast;
        const themes = ast?.children.filter((item) => item.tag == 'Theme');
        // 判断是否可使用旧ast
        const existsTheme =
          !mainRenderer?.runtime.optionsDirty &&
          themes?.some(
            (item) =>
              item.attrsMap.type == themeType && item.attrsMap.name == themeName
          );
        return existsTheme
          ? ast
          : buildAst({
              buildFor: 'editor',
              themeName,
              themeType
            });
      } else {
        const manifestXml = await service.manifest.readManifestXML();
        return xmlToAst(manifestXml ?? '');
      }
    };

    const _getImageDoms = async (ast: Ast) => {
      const _images = usingCurentWatchface
        ? imageStore.imgs
        : (await service.resource.getImagesData()) ?? [];

      const basenames = getImageSrcs(ast);

      const images: StoreResourceItem[] = [];
      _images.forEach((item) => {
        // return str.includes(item.basename);
        if (basenames.has(item.basename)) {
          images.push(item);
        }
      });

      const imgs = await imgsToDoms({
        watchfaceAst: ast,
        imgs: images,
        cached: mainRenderer?.runtime.images
      });

      return imgs;
    };

    // 使用缓存：同一 themeName+themeType 的 AST/图片/视频/描述只构建一次
    const cacheKey = `${watchfacePath ?? ''}|${themeName}|${themeType}`;
    let ast: Ast;
    let imgs: { [key: string]: HTMLImageElement };
    let videos: any;
    let desc: any;

    if (_cachedAstKey === cacheKey && _cachedAst && _cachedImgs) {
      ast = _cachedAst;
      imgs = _cachedImgs;
      videos = _cachedVideos;
      desc = _cachedDesc;
      console.log(`[screenshot-cache] HIT: ${cacheKey}`);
    } else {
      console.log(`[screenshot-cache] MISS: ${cacheKey}`);
      ast = (await _getAst()) ?? {
        tag: 'Watchface',
        attrsMap: {},
        children: []
      };
      imgs = await _getImageDoms(ast);
      const videoSrcs = getVideoSrcsFromAst(ast, watchfacePath);
      videos = await toVideoDoms(videoSrcs, { autoLoadData: true });
      desc = usingCurentWatchface
        ? watchfaceStore.description
        : await service.description.getDescriptionData();

      _cachedAstKey = cacheKey;
      _cachedAst = ast;
      _cachedImgs = imgs;
      _cachedVideos = videos;
      _cachedDesc = desc;
    }

    // 查找样式
    const theme = ast.children.find(
      (item) =>
        item.tag == 'Theme' &&
        item.attrsMap.type == themeType &&
        (themeName ? item.attrsMap.name == themeName : true)
    ) ?? {
      tag: 'Theme',
      attrsMap: {},
      children: []
    };

    // 主题配色
    const isColorTableMode = (desc?.watchfaceType ?? 'normal') == 'normal';
    const colorTable = isColorTableMode
      ? theme.attrsMap['colorGroupTable']
      : theme.attrsMap['recolorTable'];
    const colorGroupTable = colorTable?.split(',') ?? [];
    const colorIndex = colorGroupTable.findIndex((item) => item == themeColor);

    const renderTheme: WatchRendererOptions['renderTheme'] = {
      name: theme.attrsMap.name,
      type: theme.attrsMap.type as 'normal',
      colorMode: isColorTableMode ? 'colorGroup' : 'recolor',
      // 规则：当是colorGroup时，如果有多个配色，则需要添加配色值
      // 如果是recolor，当配色值为第一个配色时，无需设置配色值
      themeColor: isColorTableMode
        ? colorGroupTable.length > 1
          ? themeColor ?? colorGroupTable[0]
          : undefined
        : colorIndex > 0
        ? themeColor
        : undefined,
      slotDisplayAll,
      ignoreRotation,
      index: layerStore.themes
        .filter((t) => t.type === 'normal')
        .findIndex((item) => item.id == theme.attrsMap._id)
    };

    if (shapeIds && usingCurentWatchface) {
      const renderLayoutIds: string[] = [];

      // 将所有父级元素加上
      const shapeIds1 = [...shapeIds];
      while (shapeIds1.length) {
        const shapeId = shapeIds1.shift() ?? '';
        const shape = Shape.getShape(shapeId);
        if (!shape) continue;
        renderLayoutIds.push(shape.id);
        if (shape?.parent) shapeIds1.push(shape?.parent.id);
      }

      // 将所有子元素加上
      const shapeIds2 = [...shapeIds];
      while (shapeIds2.length) {
        const shapeId = shapeIds2.shift() ?? '';
        const shape = Shape.getShape(shapeId);
        if (!shape) continue;
        renderLayoutIds.push(shape.id);
        if (shape?.children?.length)
          shapeIds2.push(...shape.children.map((item) => item.id));
      }

      renderTheme.renderLayoutIds = [...new Set(renderLayoutIds)];
    }

    const _getDataSource = async () => {
      if (usingCurentWatchface) {
        return dataSourceStore.wholeValues(undefined);
      } else {
        const defaultValues = await service.editorConfig
          .getEditorData()
          .then((data) => data?.dataSource ?? {});

        return dataSourceStore.wholeValues(defaultValues);
      }
    };

    const defaultConfigData = await _getDataSource();

    const _getDataMaxValue = () => {
      if (!shapeIds?.length) return;
      // 递归收集所有子元素的数据源，确保 Widget 等容器类型的子元素也使用最大值
      const stack = [...shapeIds];
      while (stack.length) {
        const id = stack.shift()!;
        const shape = Shape.getShape(id);
        if (!shape) continue;
        shape.soueceKeys.forEach((key, index) => {
          const maxValue = shape.sourceItems[index]?.maxValue;
          if (maxValue) defaultConfigData[key] = maxValue;
        });
        if (shape.children?.length) {
          stack.push(...shape.children.map((c) => c.id));
        }
      }
    };

    if (useMaxValue) _getDataMaxValue();

    // 处理预览图中数据源
    if (usePreviewPointerData) {
      const __existsPointer = _existsPointer(
        layerStore.themes.find(
          (item) =>
            item.name == renderTheme.name && item.type == renderTheme.type
        )
      );
      // 如果存在指针需要使用10:09:24数据源，其他情况用09:28:00
      if (
        __existsPointer ||
        canUse('default-time-change', watchfaceStore.deviceType).valid
      ) {
        defaultConfigData['timeHour'] = 10;
        defaultConfigData['timeMinute'] = 9;
        defaultConfigData['timeSecond'] = 24;
      }
      if (canUse('default-time-change', watchfaceStore.deviceType).valid) {
        defaultConfigData['dualtimeHour'] = 10;
        defaultConfigData['dualtimeMinute'] = 9;
        defaultConfigData['dualtimeSecond'] = 24;
      }
    }

    // rive
    let rive: WatchRendererOptions['rive'] | undefined;
    if (usingCurentWatchface) {
      const theme = layerStore.themes.find(
        (item) => item.name == renderTheme.name && item.type == renderTheme.type
      );
      if (theme) {
        const _rive = await getThemeRive({
          theme,
          wathchOS: desc?.watchOS,
          renderer,
          useRivePreview
        });
        rive = _rive;
      }
    }

    const config: WatchRendererOptions = {
      ast: ast,
      images: imgs,
      videos: videos,
      configData: usingCurConfigData ? configData.value : defaultConfigData,
      renderTheme,
      scale: 1,
      ignoreBg: options?.ignoreBg,
      clipRound: options?.clipRound,
      forceShowHidden: !!forceRenderWhenHidden,
      rive
    };

    // 获取宽度和高度
    const [width, height] = desc?.size?.split('x') ?? [];
    canvasEl.width = Number(width);
    canvasEl.height = Number(height);

    renderer = renderer ?? new WatchfaceRenderer(canvasEl, config);
    if (!renderer) return;
    renderer.reset(config);

    // 绘制
    const needRendering = rive && (!rive?.rivePreviewImgEl || !useRivePreview);
    // 不需要渲染rive预览图/rive不存在预览图时，持续渲染rive本身
    if (needRendering) {
      renderer.renderring();
      await sleep(450);
    } else {
      renderer.render();
    }

    const res =
      options?.returnType == 'canvas' ? canvasEl : canvasEl.toDataURL();
    renderer?.runtime?.clear();
    WatchfaceRenderer.clearCache();

    if (timer.value) window.clearTimeout(timer.value);
    timer.value = window.setTimeout(() => {
      canvasEl = undefined;
      renderer = undefined;
      // 清理 AST/图片缓存
      _cachedAstKey = '';
      _cachedAst = undefined;
      _cachedImgs = undefined;
      _cachedVideos = undefined;
      _cachedDesc = undefined;
    }, 3000);

    return res;
  };
  onBeforeUnmount(() => {
    if (timer.value) window.clearTimeout(timer.value);
  });
  return { screenshot, canvasEl };
};
